import { useRef, useState } from "react";
import Reveal from "@/components/services/Reveal";
import { ChevronDown } from "lucide-react";

type Phase = {
  number: number;
  name: string;
  duration: string;
  deliverables: string[];
};

const PHASES: Phase[] = [
  {
    number: 1,
    name: "Mobilization",
    duration: "1 to 4 weeks",
    deliverables: [
      "Executive sponsor appointed",
      "AI steering committee formed",
      "AI program charter",
      "Preliminary acceptable use rules",
      "Current-state AI inventory",
      "Initial communication strategy",
      "Temporary controls for AI tools already in use",
    ],
  },
  {
    number: 2,
    name: "Governance foundation",
    duration: "1 to 3 months",
    deliverables: [
      "Enterprise AI policy",
      "Risk classification model",
      "Use-case intake form",
      "Initial supplier requirements",
      "Approved technology architecture",
      "Data classification rules",
      "All-employee awareness training",
      "Initial use case portfolio",
    ],
  },
  {
    number: 3,
    name: "Controlled pilots",
    duration: "3 to 6 months",
    deliverables: [
      "Three to five pilots",
      "Pilot protocols",
      "Baseline metrics",
      "User training",
      "Risk assessments",
      "Testing",
      "Pilot reports",
      "Go or no-go decisions",
      "Lessons learned review",
    ],
  },
  {
    number: 4,
    name: "Controlled scaling",
    duration: "6 to 12 months",
    deliverables: [
      "AI Center of Excellence operational",
      "Site champions in place",
      "Full procedure set",
      "Enterprise platform rollout",
      "Approved knowledge repositories",
      "GxP assurance framework",
      "Supplier qualification program",
      "Monitoring dashboard",
      "Internal audit program",
    ],
  },
  {
    number: 5,
    name: "Advanced integration",
    duration: "12 to 18 months",
    deliverables: [
      "QMS, LIMS, MES and ERP integrations",
      "Predictive maintenance",
      "Advanced process analytics",
      "Supply chain optimisation",
      "Continued process verification support",
      "Development and scale-up models",
      "Controlled regulatory evidence applications",
      "ISO/IEC 42001 readiness assessment",
    ],
  },
  {
    number: 6,
    name: "Continuous improvement",
    duration: "18 months and beyond",
    deliverables: [
      "Quarterly portfolio review",
      "Annual AI management review",
      "Periodic system reviews",
      "Regulatory surveillance",
      "Model performance monitoring",
      "AI incident trending",
      "Requalification following significant changes",
      "Retirement of low value or unacceptable systems",
    ],
  },
];

const AiRoadmapSection = () => {
  const [active, setActive] = useState(0);
  const [openPhase, setOpenPhase] = useState<number | null>(1);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const phase = PHASES[active];

  const onTabKeyDown = (e: React.KeyboardEvent) => {
    let next = active;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (active + 1) % PHASES.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (active - 1 + PHASES.length) % PHASES.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = PHASES.length - 1;
    else return;
    e.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section id="roadmap" className="bg-white py-16 sm:py-20">
      <div className="container mx-auto px-6 lg:px-10">
        <Reveal>
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-lime mb-4">
            THE ROADMAP
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
            Six phases, from first control to continuous improvement
          </h2>
          <p className="text-base sm:text-lg text-ink-muted max-w-[760px] mb-12 leading-relaxed">
            The timeline below is our recommended implementation path. Phases overlap in practice. Scope and pace adapt to your site count and starting maturity.
          </p>
        </Reveal>

        {/* Desktop stepper (>= 900px) */}
        <div className="hidden min-[900px]:block">
          <Reveal>
            <div className="relative">
              <div className="absolute left-0 right-0 top-6 h-0.5 bg-border" aria-hidden="true" />
              <div
                role="tablist"
                aria-label="AI transformation roadmap phases"
                onKeyDown={onTabKeyDown}
                className="relative grid grid-cols-6 gap-4"
              >
                {PHASES.map((p, i) => {
                  const isActive = i === active;
                  return (
                    <div key={p.number} className="flex flex-col items-center text-center">
                      <button
                        type="button"
                        role="tab"
                        id={`roadmap-tab-${p.number}`}
                        aria-selected={isActive}
                        aria-controls={`roadmap-panel-${p.number}`}
                        tabIndex={isActive ? 0 : -1}
                        ref={(el) => { tabRefs.current[i] = el; }}
                        onClick={() => setActive(i)}
                        className={`focus-ring-sky flex items-center justify-center w-12 h-12 rounded-full text-base font-semibold transition-all ${
                          isActive
                            ? "bg-brand-navy text-white ring-4 ring-accent-lime"
                            : "bg-white border border-border text-ink-muted hover:border-brand-navy"
                        }`}
                      >
                        <span className="sr-only">Phase </span>{p.number}
                      </button>
                      <span className={`mt-4 text-sm font-semibold ${isActive ? "text-brand-navy" : "text-ink-muted"}`}>
                        {p.name}
                      </span>
                      <span className="mt-1 text-xs text-ink-muted">{p.duration}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-[15px] text-ink-muted mt-8 mb-8 leading-relaxed">
              Phases 1 and 2 give you defensible control in under three months. Everything after that scales what already works.
            </p>
          </Reveal>

          {/* Detail panel */}
          <div
            key={phase.number}
            role="tabpanel"
            id={`roadmap-panel-${phase.number}`}
            aria-labelledby={`roadmap-tab-${phase.number}`}
            tabIndex={0}
            className="focus-ring-sky relative overflow-hidden bg-surface-alt rounded-xl p-10 animate-in fade-in duration-300 motion-reduce:animate-none"
          >
            <span
              className="pointer-events-none absolute top-2 right-8 font-bold leading-none select-none"
              style={{ fontSize: "120px", color: "color-mix(in oklab, var(--brand-navy) 6%, transparent)" }}
              aria-hidden="true"
            >
              {phase.number}
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-brand-navy mb-3 relative">{phase.name}</h3>
            <span
              className="inline-block text-xs font-semibold rounded-full px-3 py-1 mb-8 relative text-brand-navy"
              style={{ backgroundColor: "color-mix(in oklab, var(--accent-lime) 20%, transparent)" }}
            >
              {phase.duration}
            </span>
            <p className="text-xs font-semibold tracking-widest uppercase text-brand-navy mb-4 relative">
              Deliverables
            </p>
            <ul className="relative grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
              {phase.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3 text-sm sm:text-base text-ink-muted leading-relaxed">
                  <span
                    aria-hidden="true"
                    className="mt-2 w-1.5 h-1.5 shrink-0"
                    style={{ backgroundColor: "var(--accent-lime)" }}
                  />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Accordion below 900px */}
        <div className="min-[900px]:hidden">
          <div className="space-y-3">
            {PHASES.map((p, i) => {
              const isOpen = openPhase === p.number;
              return (
                <Reveal key={p.number} delay={i * 60} className="bg-surface-alt rounded-xl overflow-hidden">
                  <h3>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={`roadmap-acc-${p.number}`}
                      id={`roadmap-acc-trigger-${p.number}`}
                      onClick={() => setOpenPhase(isOpen ? null : p.number)}
                      className="focus-ring-sky flex w-full items-center gap-4 px-5 py-5 text-left"
                    >
                      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-brand-navy text-white text-sm font-semibold shrink-0">
                        <span className="sr-only">Phase </span>{p.number}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-base font-semibold text-brand-navy">{p.name}</span>
                        <span className="block text-xs text-ink-muted mt-0.5">{p.duration}</span>
                      </span>
                      <ChevronDown
                        size={18}
                        aria-hidden="true"
                        className={`shrink-0 text-brand-navy transition-transform duration-200 motion-reduce:transition-none ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                  </h3>
                  {isOpen && (
                    <div
                      id={`roadmap-acc-${p.number}`}
                      role="region"
                      aria-labelledby={`roadmap-acc-trigger-${p.number}`}
                      className="px-5 pb-6"
                    >
                      <p className="text-xs font-semibold tracking-widest uppercase text-brand-navy mb-3">
                        Deliverables
                      </p>
                      <ul className="space-y-2.5">
                        {p.deliverables.map((d) => (
                          <li key={d} className="flex items-start gap-3 text-sm text-ink-muted leading-relaxed">
                            <span
                              aria-hidden="true"
                              className="mt-2 w-1.5 h-1.5 shrink-0"
                              style={{ backgroundColor: "var(--accent-lime)" }}
                            />
                            {d}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Reveal>
              );
            })}
          </div>

          <p className="text-[15px] text-ink-muted mt-8 leading-relaxed">
            Phases 1 and 2 give you defensible control in under three months. Everything after that scales what already works.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AiRoadmapSection;
